import { KeyObjectStore } from '@/features/shared/tools'
import type { NavigateToDemande, NavigateToQuery } from '@/router/navigateTo'
import type { ModelDemande } from '@/features/domaine/types/opco'
import * as Locations from '@/features/application/locations/constantes.ts'
import type { SetupDemande } from '@/features/interface/demande/shared'

import { SetupDemandeAF } from '@/features/interface/demande/af/setupDemandeAF.ts'
import { SetupDemandeCAPP } from '@/features/interface/demande/capp/setupDemandeCAPP.ts'
import { SetupDemandeCPRO } from '@/features/interface/demande/cpro/setupDemandeCPRO.ts'
import { SetupDrfDemandeCXPI } from '@/features/interface/demande/cxpi/setupDrfDemandeCXPI.ts'
import { SetupDrfDossierCXPI } from '@/features/interface/demande/cxpi/setupDrfDossierCXPI.ts'
import { SetupFormationDemandeCXPI } from '@/features/interface/demande/cxpi/setupFormationDemandeCXPI.ts'
import { SetupDemandeDR } from '@/features/interface/demande/dr/setupDemandeDR.ts'
import { SetupDemandePROA } from '@/features/interface/demande/proa/setupDemandePROA.ts'
import { SetupDemandeCar } from '@/features/interface/demande/car/setupDemandeCAR.ts'
import { SetupDossierDR } from '@/features/interface/demande/dr/setupDossierDR.ts'
import { SetupDemandeOutilFPE } from '@/features/interface/demande/outilfpe/setupDemandeOutilFPE.ts'
import { SetupDemandeProforma } from '@/features/interface/demande/proforma/setupDemandeProforma.ts'
import { buildRechercheStore } from '@/features/interface/recherches/shared/composables/storeRechercheBuilder.ts'
import { useRechercheDossierStore } from '@/features/interface/recherches/dossier/useRechercheDossierStore.ts'
import { useRechercheDemandeStore } from '@/features/interface/recherches/demande/useRechercheDemandeStore.ts'
import { useDRFRechercheDossierStore } from '@/features/interface/recherches/drf/useDRFRechercheDossierStore.ts'
import { useDRFRechercheDemandeStore } from '@/features/interface/recherches/drf/useDRFRechercheDemandeStore.ts'
import { useRechercheFactureStore } from '@/features/interface/recherches/facture/useRechercheFactureStore.ts'
import { useRechercheVirementStore } from '@/features/interface/recherches/virement/useRechercheVirementStore.ts'
import {
  useDRRechercheDemandeStore,
  useDRRechercheDossierStore,
} from '@/features/interface/recherches/dr/useDRRechercheStore.ts'
import { useRechercheProformaStore } from '@/features/interface/recherches/proforma/useRechercheProformaStore.ts'
import { useRechercheDemandeTTStore } from '@/features/interface/recherches/demande-tt/useRechercheDemandeTTStore.ts'
import { useRechercheDossierTtStore } from '@/features/interface/recherches/dossier-tt/useRechercheDossierTtStore.ts'

type LocationDetailParams<TModelDemande extends ModelDemande = ModelDemande> = {
  model: TModelDemande
  setup: SetupDemande<TModelDemande>
}

type UseStoreRecherche<TRequest extends object = object, TResponse extends object = object> = ReturnType<
  typeof buildRechercheStore<TRequest, TResponse>
>
type LocationRechercheParams = {
  useStore: UseStoreRecherche
}

const keyObjectStore = new KeyObjectStore<LocationToParamsLocation, LocationToParamsResult<LocationToParamsLocation>>()
// Détail demandes et dossiers
keyObjectStore.register(Locations.LocationToFormationDemandeCAPP, {
  model: 'MODEL_CAPP',
  setup: SetupDemandeCAPP,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandeAF, {
  model: 'MODEL_AF',
  setup: SetupDemandeAF,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandeCPRO, {
  model: 'MODEL_CPRO',
  setup: SetupDemandeCPRO,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandePROA, {
  model: 'MODEL_PROA',
  setup: SetupDemandePROA,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandeCXPI, {
  model: 'MODEL_CXPI',
  setup: SetupFormationDemandeCXPI,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToDrfDemandeCXPI, {
  model: 'MODEL_CXPI',
  setup: SetupDrfDemandeCXPI,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToDrfDossierCXPI, {
  model: 'MODEL_CXPI',
  setup: SetupDrfDossierCXPI,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToDrDemande, {
  model: 'MODEL_DR',
  setup: SetupDemandeDR,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToDrDossier, {
  model: 'MODEL_DR',
  setup: SetupDossierDR,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandeFPE_AF, {
  model: 'MODEL_AF',
  setup: SetupDemandeAF,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToDemandeOutilFPE, {
  model: 'MODEL_OUTIL_FPE',
  setup: SetupDemandeOutilFPE,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToProformaDemande, {
  model: 'MODEL_PROFORMA',
  setup: SetupDemandeProforma,
} as LocationDetailParams)
keyObjectStore.register(Locations.LocationToFormationDemandeCAR, {
  model: 'MODEL_CAR',
  setup: SetupDemandeCar,
} as LocationDetailParams)

// Recherches demandes et dossiers
keyObjectStore.register(Locations.LocationToRechercheFormationDossiers, { useStore: useRechercheDossierStore })
keyObjectStore.register(Locations.LocationToRechercheFormationDemandes, { useStore: useRechercheDemandeStore })
keyObjectStore.register(Locations.LocationToRechercheDrfDossiersCXPI, { useStore: useDRFRechercheDossierStore })
keyObjectStore.register(Locations.LocationToRechercheDrfDemandesCXPI, { useStore: useDRFRechercheDemandeStore })
keyObjectStore.register(Locations.LocationToRechercheFactures, { useStore: useRechercheFactureStore })
keyObjectStore.register(Locations.LocationToRechercheVirements, { useStore: useRechercheVirementStore })
keyObjectStore.register(Locations.LocationToRechercheDrDossiers, { useStore: useDRRechercheDossierStore })
keyObjectStore.register(Locations.LocationToRechercheDrDemandes, { useStore: useDRRechercheDemandeStore })
keyObjectStore.register(Locations.LocationToRechercheProformaDemandes, { useStore: useRechercheProformaStore })
keyObjectStore.register(Locations.LocationToRechercheOutilFPEDemandes, { useStore: useRechercheDemandeTTStore })
keyObjectStore.register(Locations.LocationToRechercheOutilFPEDossiers, { useStore: useRechercheDossierTtStore })

export type LocationToDetail = Omit<NavigateToDemande, 'id' | 'code'>
export type LocationToRecherche = Omit<NavigateToQuery, 'query' | 'orderBy' | 'pagination'>

type LocationToParamsLocation = LocationToDetail | LocationToRecherche
type LocationToParamsResult<T extends LocationToParamsLocation> = T extends LocationToDetail
  ? LocationDetailParams
  : T extends LocationToRecherche
    ? LocationRechercheParams
    : never

export function locationToParams<T extends LocationToParamsLocation>(location: T) {
  return keyObjectStore.getData(location as NavigateToDemande) as LocationToParamsResult<T>
}

export function tryLocationToParams<T extends LocationToParamsLocation>(location: T) {
  return keyObjectStore.tryGetData(location as NavigateToDemande) as LocationToParamsResult<T> | undefined
}
